import React from 'react';

/**
 * CellWorkspaceView — a cell's workspace as it IS: the place it runs in, or the rule that will pick
 * one.
 *
 * The READ-ONLY half of the cell panel's Workspace section, and the exact counterpart of
 * CellWorkflowView beside it. The two sections describe the same shape of decision — a MODE, plus
 * whatever that mode resolved to — so they are rendered the same way, by two views written the
 * same way.
 *
 * It exists because the Workspace section rendered NOTHING when a cell had no workspace of its own:
 * a section label with a gap under it, immediately followed by the next heading. That is the
 * ordinary state of a draft cell in either inherit mode, and Cell Workflow directly above it
 * answered the same situation with "Inherit from mission" — so the panel was saying, of two
 * identical facts, one of them and not the other.
 *
 * No pickers, and no help text about choosing: once a workspace is frozen there is no choice left
 * to describe, and before it is resolved the mode IS the description.
 *
 * Props:
 *   cell — cell vertex object ({ props })
 */

// Human-readable labels for WORKSPACE_MODES (schema.js keeps the wire values snake_case). They live
// beside the read-only view for the reason the cell-workflow ones do: the panel's mode picker and
// this view name the same three modes, and two spellings of one vocabulary is how they drift.
export const WORKSPACE_MODE_LABELS = {
  inherit_from_mission: 'Inherit from mission',
  specify_workspace: 'Specify workspace',
  inherit_from_previous_cell: 'Inherit from previous cell',
};

// What to say when the cell carries no workspace of its own. Both inherit modes ARE the answer —
// they name where the copy will come from at dispatch. `specify_workspace` with nothing picked is
// not an answer, so it reads as the nothing it is.
function unresolved(mode) {
  if (!mode || mode === 'specify_workspace') return 'None';
  return WORKSPACE_MODE_LABELS[mode] || mode;
}

export default function CellWorkspaceView({ cell }) {
  const props = (cell && cell.props) || {};
  const workspace = props.workspace || null;

  if (!workspace) {
    return <p className="mb-0">{unresolved(props.workspace_mode)}</p>;
  }

  // The workspace is an ACTUAL place, not a template, so what matters is its type and that type's
  // own fields. `local_existing` has one — the folder — and a richer type added behind the same
  // seam gets its own row here without changing anything else.
  return (
    <table className="properties">
      <tbody>
        <tr>
          <td className="prop-name-column">Type</td>
          <td className="prop-value-column"><code>{workspace.type || 'unknown'}</code></td>
        </tr>
        {workspace.work_folder && (
          <tr>
            <td className="prop-name-column">Folder</td>
            <td className="prop-value-column"><code>{workspace.work_folder}</code></td>
          </tr>
        )}
      </tbody>
    </table>
  );
}
